/* eslint-disable no-param-reassign */
import React, {
	forwardRef,
	useEffect,
	useRef,
	useState,
	memo,
	useMemo,
} from 'react';
import Hls from 'hls.js';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import { LOCATION_CANVAS_ROUTES } from '../../stores/reducers/video';
import { useAudioSink } from '../../hooks/devices/use-audio-sink';
import logger from '../../utils/logger-rum';

type ProgressUpdate = {
	played: number;
	playedSeconds: number;
	loaded: number;
	loadedSeconds: number;
};

export type CrossOrigin = '' | 'anonymous' | 'use-credentials' | undefined;

type CustomMediaVideoProps = {
	id?: string;
	src: string;
	isHlsVideo: boolean;
	className?: string;
	duration?: number;
	elapsed: number;
	onEnded?: () => void;
	onError?: (error: Error) => void;
	onPlay?: () => void;
	onProgress?: (state: ProgressUpdate) => void;
	muted?: boolean;
	crossOrigin: CrossOrigin;
	loop?: boolean;
	posterImage?: string;
	autoPlay?: boolean;
	controlsOn?: boolean;
	forcePoster?: boolean
};

// Add custom props to all the optional props a video element could have
type MediaVideoProps = CustomMediaVideoProps & Partial<React.VideoHTMLAttributes<HTMLVideoElement>>;

const MediaVideo = forwardRef(
	(
		{
			src,
			isHlsVideo,
			className,
			duration,
			elapsed,
			onEnded,
			onError,
			onPlay,
			onProgress,
			muted = false,
			crossOrigin,
			loop = false,
			posterImage,
			autoPlay = false,
			controlsOn = false,
			forcePoster = false,
			...otherProps
		}: MediaVideoProps,
		forwardVideoRef: any,
	): JSX.Element => {
		const hlsRef = useRef<Hls | null>(null);
		const timeUpdateRef = useRef<ProgressUpdate>({
			played: 0,
			playedSeconds: 0,
			loaded: 0,
			loadedSeconds: 0,
		});
		const [subsequentUnmount, setSubsequentUnmount] = useState(false);
		const [showPoster, setShowPoster] = useState(true);
		const companionMode = useSelector((s: GS) => s.SessionReducer.companionMode);
		const onsiteLocationSession = useSelector((s: GS) => s.VideoReducer.onsiteLocationSession);
		const modalOpen = useSelector((s: GS) => s.ModalReducer.modalOpen);
		const { applySinkId } = useAudioSink();

		const isInitialOnsiteLocationCanvas = useMemo(
			() => !!onsiteLocationSession
				&& LOCATION_CANVAS_ROUTES.includes(onsiteLocationSession.route)
				&& modalOpen,
			[onsiteLocationSession, modalOpen],
		);

		useEffect(() => {
			// Currently ivs creates an extra bit of duration for hls videos created
			// if the playSeconds is larger than the duration then we're at the end of the video.
			const oneFrame = 1 / 24;
			if (
				duration
				&& duration !== 0
				&& timeUpdateRef.current.playedSeconds > duration - oneFrame
				&& isHlsVideo
			) {
				if (loop) {
					forwardVideoRef.current.seekTo(0);
				} else {
					onEnded?.();
				}
			}
		}, [
			duration,
			forwardVideoRef,
			isHlsVideo,
			loop,
			onEnded,
			timeUpdateRef.current.playedSeconds,
		]);

		useEffect(() => {
			if (isHlsVideo && forwardVideoRef?.current && src) {
				// Destroy existing instance before creating new one
				if (hlsRef.current) {
					try {
						hlsRef.current.stopLoad();
						hlsRef.current.destroy();
					} catch (err) {
						// Ignore SourceBuffer removed errors during cleanup
						if (!(err instanceof DOMException && err.message.includes('SourceBuffer'))) {
							logger.error('Error destroying HLS instance:', err);
						}
					}
				}

				hlsRef.current = new Hls();
				hlsRef.current.loadSource(src);
				hlsRef.current.attachMedia(forwardVideoRef.current);

				return () => {
					if (isHlsVideo && hlsRef.current) {
						try {
							hlsRef.current.stopLoad();
							hlsRef.current.destroy();
						} catch (err) {
							// Ignore SourceBuffer removed errors during cleanup
							if (!(err instanceof DOMException && err.message.includes('SourceBuffer'))) {
								logger.error('Error destroying HLS instance:', err);
							}
						}
					}
				};
			}
			return undefined;
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [src, isHlsVideo]);

		useEffect(() => () => {
			if (subsequentUnmount) {
				if (isHlsVideo && hlsRef.current) {
					try {
						hlsRef.current.stopLoad();
						hlsRef.current.destroy();
					} catch (err) {
						// Ignore SourceBuffer removed errors during cleanup
						if (!(err instanceof DOMException && err.message.includes('SourceBuffer'))) {
							logger.error('Error destroying HLS instance:', err);
						}
					}
				}
			}
			setSubsequentUnmount(true);
		}, [subsequentUnmount]);

		useEffect(() => {
			onProgress?.(timeUpdateRef.current);
		}, [onProgress]);

		useEffect(() => {
			if (showPoster && elapsed > 0) {
				setShowPoster(false);
			}
		}, [elapsed]);

		// Apply setSinkId to video element
		useEffect(() => {
			if (forwardVideoRef?.current) {
				applySinkId(forwardVideoRef.current);
			}
		}, [applySinkId, forwardVideoRef?.current]);

		return (
			<div
				className={classNames('media-video-container', 'flex-center', {
					'ivs-player-container': isHlsVideo,
				})}
			>
				<video
					className={classNames(className, { 'ivs-player-video': isHlsVideo })}
					src={!isHlsVideo ? src : undefined}
					poster={(showPoster || forcePoster) && posterImage ? posterImage : undefined}
					crossOrigin={crossOrigin}
					playsInline={true}
					data-testid='videoplayer'
					ref={forwardVideoRef}
					loop={loop}
					muted={muted || companionMode || isInitialOnsiteLocationCanvas}
					autoPlay={autoPlay}
					controls={controlsOn}
					{...{
						onEnded,
						onError,
						onPlay,
						onProgress,
					}}
					/*
						Pass all of the other video props not listed directly
						above through to the video element.

						This includes `CustomMediaVideoProps` as well as all
						`HTMLVideoElement` props, so this can be treated like
						an extended `<video />` element.
					*/
					{...otherProps}
					onPlaying={() => {
						if (showPoster) {
							setShowPoster(false);
						}
					}}
				/>

				{posterImage && (showPoster || forcePoster) && (
					<div className='media-video-poster'>
						<img src={posterImage} alt={''} />
					</div>
				)}
			</div>
		);
	},
);

MediaVideo.displayName = 'MediaVideo';

export default memo(MediaVideo);
